import moment from "moment";
import {
  ConversationContact,
  Message,
  Timestamp,
} from "../interfaces/conversations.interface";
import { ContentMessageType } from "../types/whatsapp-message.types";

const KNOWN_TYPES: ContentMessageType[] = [
  "text",
  "image",
  "audio",
  "video",
  "document",
  "template",
  "hsm",
];

function coerceTimestamp(raw: unknown): Timestamp {
  if (raw && typeof raw === "object" && "full" in (raw as object)) {
    const t = raw as Partial<Timestamp>;
    if (t.full) {
      return {
        full: String(t.full),
        date: t.date ?? "",
        time: t.time ?? "",
      };
    }
  }
  const iso =
    typeof raw === "string"
      ? raw
      : raw &&
          typeof raw === "object" &&
          "createdAt" in (raw as object) &&
          (raw as { createdAt?: string }).createdAt
        ? String((raw as { createdAt?: string }).createdAt)
        : null;
  if (iso) {
    const m = moment(iso);
    if (m.isValid()) {
      return {
        full: m.toISOString(),
        date: m.format("YYYY-MM-DD"),
        time: m.format("HH:mm:ss"),
      };
    }
  }
  const now = moment();
  return {
    full: now.toISOString(),
    date: now.format("YYYY-MM-DD"),
    time: now.format("HH:mm:ss"),
  };
}

function coerceFrom(raw: unknown): ConversationContact {
  if (!raw) {
    return { id: "", name: "?" };
  }
  if (typeof raw === "object" && raw !== null && "id" in raw) {
    const c = raw as ConversationContact;
    return {
      id: String(c.id),
      name: c.name ?? "",
      avatarUrl: c.avatarUrl,
      email: c.email,
    };
  }
  const r = raw as Record<string, unknown> | null | undefined;
  const id = r?.id ?? r?.senderId ?? "";
  return {
    id: String(id ?? ""),
    name: String(r?.name ?? r?.senderName ?? r?.username ?? "?"),
    avatarUrl: r?.avatarUrl as string | undefined,
  };
}

/**
 * Extrae un array de mensajes del body HTTP aunque venga en distintas formas
 * (payload.messages, messages, data, etc.).
 */
export function extractMessagesArray(raw: unknown): unknown[] {
  if (raw == null) return [];
  if (Array.isArray(raw)) return raw;
  if (typeof raw !== "object") return [];
  const o = raw as Record<string, unknown>;

  if (Array.isArray(o.payload)) return o.payload;

  const tryKeys = (obj: unknown): unknown[] | null => {
    if (!obj || typeof obj !== "object") return null;
    const x = obj as Record<string, unknown>;
    for (const k of ["messages", "data", "items", "records", "results"]) {
      const v = x[k];
      if (Array.isArray(v)) return v;
    }
    return null;
  };

  if (o.payload) {
    const fromNested = tryKeys(o.payload);
    if (fromNested !== null) return fromNested;
  }

  const fromRoot = tryKeys(o);
  if (fromRoot !== null) return fromRoot;

  if (Array.isArray(o.data)) return o.data;

  return [];
}

/**
 * Convierte un ítem del API al modelo Message (ids string, timestamp, from, type).
 */
export function normalizeSocialMessage(raw: unknown, index: number): Message {
  if (raw == null || typeof raw !== "object") {
    return {
      id: `social-msg-${index}-empty`,
      message: "",
      type: "text",
      from: coerceFrom(null),
      timestamp: coerceTimestamp(null),
    };
  }
  const r = raw as Record<string, unknown>;

  const body =
    typeof r?.message === "string"
      ? r.message
      : typeof r?.text === "string"
        ? r.text
        : typeof r?.body === "string"
          ? r.body
          : typeof r?.content === "string"
            ? r.content
            : "";

  const typeRaw = r?.type ?? r?.messageType ?? "text";
  const typeLower = String(typeRaw).toLowerCase();
  const type: ContentMessageType = KNOWN_TYPES.includes(
    typeLower as ContentMessageType
  )
    ? (typeLower as ContentMessageType)
    : "text";

  const id =
    r?.id != null
      ? String(r.id)
      : r?.messageId != null
        ? String(r.messageId)
        : r?._id != null
          ? String(r._id)
          : `social-msg-${index}-${Date.now()}`;

  const fromSource = r.from ?? r.sender ?? r.participant;

  return {
    id,
    message: body,
    type,
    from: coerceFrom(fromSource),
    timestamp: coerceTimestamp(r.timestamp ?? r.createdAt ?? r),
  };
}
